/**
 * Reusable sidebar filter card shell: header, selected chip(s), and a portaled popover.
 * Renders popover body from filterControlDataProps — callers do not compose content as children.
 * Presentational only — no store access.
 */
import React, { useRef } from "react";
import RmaListFilterControlChips, {
    IRmaListFilterControlChip,
} from "./rma-list-filter-control-chips/rma-list-filter-control-chips";
import RmaListFilterControlHeader from "./rma-list-filter-control-header/rma-list-filter-control-header";
import { RmaListFilterControlDataProps } from "./rma-list-filter-control-portal/rma-list-filter-control-content/rma-list-filter-control-content";
import RmaListFilterControlPortal from "./rma-list-filter-control-portal/rma-list-filter-control-portal";
import { useRmaListFilterPopover } from "./use-rma-list-filter-popover";
import "./rma-list-filter-control.scss";

interface IProps {
    label: string;
    /** Single selected value chip (Date created and other single-select filters). */
    selectedLabel?: string;
    /** Multiple selected chips when a filter needs more than one removable value. */
    selectedChips?: IRmaListFilterControlChip[];
    onClear?: () => void;
    onClearChip?: (chipId: string) => void;
    disabled?: boolean;
    onOpen?: () => void;
    filterControlDataProps: RmaListFilterControlDataProps;
}

const RmaListFilterControl: React.FC<IProps> = ({
    label,
    selectedLabel,
    selectedChips,
    onClear,
    onClearChip,
    disabled,
    onOpen,
    filterControlDataProps,
}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const { isOpen, close, toggle, } = useRmaListFilterPopover({ disabled, onOpen, });

    const chips: IRmaListFilterControlChip[] =
        selectedChips ??
        (selectedLabel
            ? [{ id: "selected", label: selectedLabel, },]
            : []);

    const onClearChipClick = (event: React.MouseEvent, chipId: string) => {
        event.stopPropagation();
        if (disabled) {
            return;
        }
        if (onClearChip) {
            onClearChip(chipId);
        } else {
            onClear?.();
        }
        close();
    };

    return (
        <div className={`rma-list-filter-control${disabled ? " rma-list-filter-control--disabled" : ""}`}>
            <div
                ref={cardRef}
                className={`rma-list-filter-control__card${
                    isOpen ? " rma-list-filter-control__card--open" : ""
                }`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-expanded={isOpen}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (disabled) {
                        return;
                    }
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <RmaListFilterControlHeader label={label} />
                <RmaListFilterControlChips
                    chips={chips}
                    disabled={disabled}
                    onClearChip={onClearChipClick}
                />
            </div>

            <RmaListFilterControlPortal
                isOpen={isOpen}
                anchorRef={cardRef}
                filterControlDataProps={filterControlDataProps}
                onClose={close}
            />
        </div>
    );
};

export default RmaListFilterControl;
